import { useGrowthBook } from '@growthbook/growthbook-react';
import { useAtomValue, useSetAtom } from 'jotai';
import { useRouter } from 'next/router';
import { useCallback, useEffect } from 'react';

import { derivePaymentReturnStatus } from '@/pages/subscribe/paymentReturnStatus';

import {
  authDialogContextAtom,
  authEntrySceneAtom,
  downloadStageContextAtom,
  signupPlanStepAtom,
} from './atom';
import { PRICING_SIGNUP_QUESTIONNAIRE_KEY } from './pricingSignupFlow';
import { useStage } from './useStage';

import type { DownloadSignupPlanArm, DownloadStageContext } from './atom';

/**
 * Plan-step sign-up experiment (DEV-7090 / DEV-7091). A GrowthBook feature
 * whose experiment rule splits general sign-ups 50/50; `true` is the Variant,
 * which sees `onboarding.plan-step` between account creation and the
 * questionnaire. Assignment is sticky via GrowthBook's cookie bucketing on
 * the device id, so reopening the modal keeps the group.
 */
export const SIGNUP_PLAN_STEP_FEATURE = 'signup-plan-step';

/**
 * Free-asset download sign-up test (DEV-7278): a GrowthBook feature whose
 * experiment rule splits 33/33/33 on the device id. `control` is today's
 * journey; `plan-step` and `plan-choice` are the {@link DownloadSignupPlanArm}s. Evaluated at
 * the Download click so every arm is exposed from the same point.
 */
export const DOWNLOAD_SIGNUP_PLAN_FEATURE = 'download-signup-plan-choice';

/** Checkout conversion source per arm, so plan-card checkouts attribute to it. */
export const DOWNLOAD_PLAN_CONVERSION_SOURCE: Record<
  DownloadSignupPlanArm,
  string
> = {
  'plan-step': 'downloadFlow_signupPlanStep',
  'plan-choice': 'downloadFlow_signupPlanChoice',
};

export type SignupMethod = 'google' | 'email';

/**
 * Set on a plan-card click, so a return to any page other than `/subscribe`
 * reopens the plan step (abandoned checkout). sessionStorage for the same
 * reason as `PRICING_SIGNUP_QUESTIONNAIRE_KEY`: offsite payment methods
 * reload the page.
 */
const CHECKOUT_PENDING_KEY = 'signupPlanStepCheckoutMethod';

// Sign-ups that already carry a pricing decision (a pricing-page plan pick)
// or belong to another journey stay out of the test.
const EXCLUDED_CONTEXTS = new Set([
  'pricing-plan',
  'pricing-free-plan',
  'partner-application',
]);

/**
 * Returns `arm(method)` for a sign-up scene's success handler. For a general
 * sign-up it evaluates the experiment, which fires GrowthBook's
 * `experiment_viewed` exposure, so only completed sign-ups enter the test;
 * a Variant sign-up is flagged so `onboarding.auth-gate` routes it to the
 * plan step. Only the general auth flow sets `authEntrySceneAtom`; download
 * journeys reach `auth.signup-email` from `download.signup`, so they never
 * qualify.
 */
export const useSignupPlanStep = () => {
  const growthbook = useGrowthBook();
  const entry = useAtomValue(authEntrySceneAtom);
  const context = useAtomValue(authDialogContextAtom);
  const setPlanStep = useSetAtom(signupPlanStepAtom);
  const isGeneralSignup =
    entry !== null && !(context && EXCLUDED_CONTEXTS.has(context));

  return useCallback(
    (method: SignupMethod) => {
      if (isGeneralSignup && growthbook?.isOn(SIGNUP_PLAN_STEP_FEATURE)) {
        setPlanStep({ method, flow: 'general' });
      }
    },
    [isGeneralSignup, growthbook, setPlanStep]
  );
};

type PendingCheckout = {
  method: SignupMethod;
  /** Set on the download flow, so an abandoned checkout can resume it. */
  download?: DownloadStageContext;
};

/** Called right before a plan-step card sends the user to checkout. */
export const markPlanStepCheckoutPending = (
  method: SignupMethod,
  download?: DownloadStageContext
) => {
  sessionStorage.setItem(
    CHECKOUT_PENDING_KEY,
    JSON.stringify({ method, download } satisfies PendingCheckout)
  );
  // The download flow's success screen reopens the download instead.
  if (!download) {
    // Reuses the pricing-card journey's hand-off: the /subscribe success
    // screen opens the questionnaire once the user is back on their page.
    sessionStorage.setItem(PRICING_SIGNUP_QUESTIONNAIRE_KEY, '1');
  }
};

const readPendingCheckout = (): PendingCheckout | null => {
  try {
    const pending = JSON.parse(
      sessionStorage.getItem(CHECKOUT_PENDING_KEY) ?? 'null'
    ) as PendingCheckout | null;
    return pending?.method === 'google' || pending?.method === 'email'
      ? pending
      : null;
  } catch {
    return null;
  }
};

/**
 * Reopens the plan step when a user who left it for checkout lands on any
 * page other than `/subscribe` (back button, logo link): the checkout was
 * abandoned. A completed checkout clears the marker on the success screen,
 * which then hands off to the questionnaire itself.
 */
export const usePlanStepCheckoutReturn = () => {
  const router = useRouter();
  const { open } = useStage();
  const setPlanStep = useSetAtom(signupPlanStepAtom);
  const setDownloadContext = useSetAtom(downloadStageContextAtom);

  useEffect(() => {
    if (!router.isReady) {
      return;
    }
    const pending = readPendingCheckout();
    if (!pending) {
      return;
    }
    if (router.pathname.startsWith('/subscribe')) {
      if (derivePaymentReturnStatus(router.query).isOnSuccessScreen) {
        sessionStorage.removeItem(CHECKOUT_PENDING_KEY);
      }
      return;
    }
    sessionStorage.removeItem(CHECKOUT_PENDING_KEY);
    // The next card click re-arms it; a stale flag would open the
    // questionnaire after some later, unrelated checkout.
    sessionStorage.removeItem(PRICING_SIGNUP_QUESTIONNAIRE_KEY);
    const { method, download } = pending;
    if (download) {
      // Own copy: usePostPaymentDownload clears the pending download intent
      // on this same page load.
      setDownloadContext({ ...download, plan: undefined });
    }
    setPlanStep({
      method,
      flow: download ? 'download-plan-step' : 'general',
    });
    open('onboarding.plan-step');
  }, [
    router.isReady,
    router.pathname,
    router.query,
    open,
    setPlanStep,
    setDownloadContext,
  ]);
};
